import { Button, DatePicker, Drawer, Grid, Space, Typography, useDrawerForm } from '@pankod/refine-antd'
import { useTranslate } from '@pankod/refine-core'
import { definitions } from 'interfaces'
import { useState } from 'react'
import { CURRENT_DATE_TIME, generateHorizontalAudit } from 'utility'
import { utils, writeFile } from 'xlsx'

type horizontalAuditButtonProps = {
  filename: string
}

export const HorizontalAuditButton = ({ filename }: horizontalAuditButtonProps) => {
  const t = useTranslate()
  const breakpoint = Grid.useBreakpoint()
  filename = `${filename} ${CURRENT_DATE_TIME}.xlsx`

  const {
    drawerProps: createDrawerProps,
    close: createClose,
    show: createShow
  } = useDrawerForm<definitions['orders']>({
    action: 'create',
    redirect: false
  })

  const { RangePicker } = DatePicker

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [dates, setDates] = useState<any[]>([])

  const generateAuditReport = async () => {
    const headerNames = {
      id: t('order.horizontalAudit.idOrder'),
      user_id: t('order.horizontalAudit.idUser'),
      device: t('order.horizontalAudit.device'),
      status: t('order.horizontalAudit.idStatus'),
      mo_date: t('order.horizontalAudit.moDate'),
      marketplace: t('order.horizontalAudit.marketplace'),
      purchase_amount: t('order.horizontalAudit.purchaseAmount'),
      shipment_method: t('order.horizontalAudit.shipmentMethod'),
      shipper_id: t('order.horizontalAudit.idTracking'),
      pending_payment: t('order.status.0'),
      requested: t('order.status.1'),
      processed: t('order.status.2'),
      cancelled: t('order.status.3'),
      returned: t('order.status.4'),
      supplement: t('order.status.5'),
      admitted: t('order.status.6'),
      no_stock: t('order.status.7'),
      prepared: t('order.status.8'),
      sent: t('order.status.9'),
      delivered: t('order.status.10'),
      penalty: t('order.status.11'),
      credit: t('order.status.12'),
      cancel_request: t('order.horizontalAudit.solCancelled'),
      return_request: t('order.horizontalAudit.solReturned'),
      sup_date: t('order.horizontalAudit.supDate'),
      transport_pending: t('order.horizontalAudit.suptrpp'),
      transport_admitted: t('order.horizontalAudit.suptrad'),
      price_pending: t('order.horizontalAudit.suppvppp'),
      price_admitted: t('order.horizontalAudit.suppvpad'),
      iva_pending: t('order.horizontalAudit.supivapp'),
      iva_admitted: t('order.horizontalAudit.supivaad'),
      other_pending: t('order.horizontalAudit.supotherpp'),
      other_admitted: t('order.horizontalAudit.supotherad'),
      dua_pending: t('order.horizontalAudit.supduapp'),
      dua_admitted: t('order.horizontalAudit.supduaad'),
      tariff_pending: t('order.horizontalAudit.suptariffpp'),
      tariff_admitted: t('order.horizontalAudit.suptariffad'),
      igic_pending: t('order.horizontalAudit.supigicpp'),
      igic_admitted: t('order.horizontalAudit.supigicad'),
      vol_pending: t('order.horizontalAudit.suppp'),
      vol_admitted: t('order.horizontalAudit.supad'),
      prov_transit: t('order.horizontalAudit.prov_transit'),
      syniva_transit: t('order.horizontalAudit.syniva_transit'),
      syniva_sent: t('order.horizontalAudit.syniva_sent'),
      full_sent: t('order.horizontalAudit.full_sent'),
      amount: t('order.horizontalAudit.supAmount')
    }

    const data = await generateHorizontalAudit(dates)

    if (!data || data.length === 0) {
      return null
    }

    data.unshift(headerNames)
    return data || []
  }

  const autofitColumns = (json: any[]) => {
    const jsonKeys = Object.keys(json[0])
    const objectMaxLength: any = []

    for (let i = 0; i < json.length; i++) {
      const value = json[i]
      for (let j = 0; j < jsonKeys.length; j++) {
        if (typeof value[jsonKeys[j]] === 'number') {
          objectMaxLength[j] = 10
        } else {
          const l = value[jsonKeys[j]] ? value[jsonKeys[j]].length : 0
          objectMaxLength[j] = objectMaxLength[j] >= l ? objectMaxLength[j] : l
        }
      }

      const key = jsonKeys
      for (let j = 0; j < key.length; j++) {
        objectMaxLength[j] = objectMaxLength[j] >= key[j].length ? objectMaxLength[j] : key[j].length
      }
    }

    return objectMaxLength.map((w: number) => {
      return { width: w }
    })
  }

  const exportToExcel = async () => {
    setLoading(true)
    const jsonData = await generateAuditReport()
    if (jsonData === null) {
      setError(true)
    } else {
      const worksheet = utils.json_to_sheet(jsonData, { skipHeader: true })
      const workbook = utils.book_new()
      utils.book_append_sheet(workbook, worksheet, 'Sheet1')
      worksheet['!cols'] = autofitColumns(jsonData)
      setLoading(false)
      writeFile(workbook, filename)

      createClose()
    }
  }

  const changeDates = (datesInput: any) => {
    const arrayDates = [datesInput[0], datesInput[1]]
    const dateStart = `${arrayDates[0].get('year')}-${(arrayDates[0].get('month') + 1 > 9) ? arrayDates[0].get('month') + 1 : '0' + (arrayDates[0].get('month') + 1)}-${(arrayDates[0].get('date') > 9) ? arrayDates[0].get('date') : '0' + arrayDates[0].get('date')}`
    const dateEnd = `${arrayDates[1].get('year')}-${(arrayDates[1].get('month') + 1 > 9) ? arrayDates[1].get('month') + 1 : '0' + (arrayDates[1].get('month') + 1)}-${(arrayDates[1].get('date') > 9) ? arrayDates[1].get('date') : '0' + arrayDates[1].get('date')}`
    setDates([dateStart, dateEnd])
  }

  return <>
    <Button type="primary" onClick={() => createShow()}>
      {t('order.horizontalAudit.button')}
    </Button>
    <Drawer
      {...createDrawerProps}
      width={breakpoint.sm ? '800px' : '100%'}
      bodyStyle={{ padding: 0 }}
      zIndex={1001}
    >
      <Space style={{ width: '100%', display: 'grid', marginTop: '5%', marginLeft: '10%' }} direction="vertical" size={12}>
        <Typography.Text>
          {t('order.horizontalAudit.labelDate')}
        </Typography.Text>
        <RangePicker showTime={false} onChange={changeDates} />

        <Button style={{ marginTop: '1%' }} type="primary" loading={loading} onClick={() => exportToExcel()}>
          {t('order.horizontalAudit.generateReport')}
        </Button>
      </Space>
      {(error)
        ? <Space style={{ width: '100%', display: 'grid', marginTop: '5%', marginLeft: '10%' }} direction="vertical" size={12}>
          <Typography.Text>
            {t('order.horizontalAudit.error')}
          </Typography.Text>
        </Space>
        : null}
    </Drawer>
  </>
}
